import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
const resolveUrl = (url) => url?.startsWith('/api/uploads/') ? `${BASE_URL}${url}` : url;

const DEFAULT_HERO = 'https://images.pexels.com/photos/10543051/pexels-photo-10543051.jpeg?auto=compress&cs=tinysrgb&w=1920';

export default function HeroSection() {
  const [heroImage, setHeroImage] = useState(DEFAULT_HERO);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API}/settings`);
        if (response.ok) {
          const data = await response.json();
          if (data.hero_image_url) setHeroImage(resolveUrl(data.hero_image_url));
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };
    fetchSettings();
  }, []);
  const scrollToContact = () => {
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToBreed = () => {
    document.querySelector("#race")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="accueil"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 hero-parallax"
        style={{
          backgroundImage: `url('${heroImage}')`,
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-forest/60 via-forest/40 to-forest/70" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
        <p
          className="font-accent text-2xl md:text-3xl text-sand mb-4 opacity-0 animate-fade-in-up"
          style={{ animationDelay: "0.2s", animationFillMode: "forwards" }}
        >
          Bienvenue chez
        </p>
        
        <h1
          className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 opacity-0 animate-fade-in-up"
          style={{ animationDelay: "0.4s", animationFillMode: "forwards" }}
        >
          La Taïga Siberia
        </h1>
        
        <p
          className="font-body text-lg md:text-xl text-cream/90 mb-4 max-w-2xl mx-auto opacity-0 animate-fade-in-up"
          style={{ animationDelay: "0.6s", animationFillMode: "forwards" }}
        >
          Élevage familial de chats Sibériens traditionnels
        </p>
        
        <p
          className="font-body text-base md:text-lg text-sand/80 mb-10 max-w-3xl mx-auto opacity-0 animate-fade-in-up"
          style={{ animationDelay: "0.8s", animationFillMode: "forwards" }}
        >
          Des compagnons d'exception, élevés avec amour et passion dans un cadre familial,
          pour apporter joie et douceur à votre foyer.
        </p>

        <div
          className="flex flex-col sm:flex-row gap-4 justify-center opacity-0 animate-fade-in-up"
          style={{ animationDelay: "1s", animationFillMode: "forwards" }}
        >
          <Button
            onClick={scrollToContact}
            className="btn-shine bg-autumn hover:bg-autumn-dark text-white font-body font-semibold text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-hover transition-all duration-300"
            data-testid="hero-contact-btn"
          >
            Nous contacter
          </Button>
          <Button
            onClick={scrollToBreed}
            variant="outline"
            className="border-2 border-white/80 text-white hover:bg-white/10 font-body font-semibold text-lg px-8 py-6 rounded-full transition-all duration-300"
            data-testid="hero-discover-btn"
          >
            Découvrir la race
          </Button>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 opacity-0 animate-fade-in"
        style={{ animationDelay: "1.5s", animationFillMode: "forwards" }}
      >
        <button
          onClick={scrollToBreed}
          className="flex flex-col items-center text-white/70 hover:text-white transition-colors"
          data-testid="scroll-indicator"
        >
          <span className="font-body text-sm mb-2">Découvrir</span>
          <ChevronDown className="h-6 w-6 animate-bounce" />
        </button>
      </div>
    </section>
  );
}
